import { z } from 'zod'

import { PermissionSchema } from '../permissions'
import { WhatsappTargetSchema } from '../customer-service'
import { DEFAULT_NOTIFICATION_PREFERENCES, NotificationPreferencesSchema } from '../notifications'
import { RoleKeyStringSchema } from '../roles'
import { emailSchema, isoDateTimeSchema } from './common'

export const THEME_PREFERENCES = ['light', 'dark', 'system'] as const
export type ThemePreference = (typeof THEME_PREFERENCES)[number]

export const DENSITY_PREFERENCES = ['comfortable', 'compact'] as const
export type DensityPreference = (typeof DENSITY_PREFERENCES)[number]

/** Chave de um item da lista Primeiros passos (`first-steps` no web), em minúsculas e pontos. */
export const ONBOARDING_STEP_KEY_PATTERN = /^[a-z][a-z0-9-]*(\.[a-z0-9-]+)*$/
/** Teto de itens gravados: a lista tem bem menos; o teto só impede um bloco inflado. */
export const ONBOARDING_MAX_STEPS = 64
/**
 * Versão do onboarding: sobe quando o onboarding muda o bastante para ser mostrado de novo a quem
 * já o viu. Bloco sem `version` vale 1.
 */
export const ONBOARDING_VERSION = 2

/**
 * Onboarding guiado (boas-vindas, tour e lista Primeiros passos). Datas ISO; `steps` guarda a
 * data em que cada item ficou feito, pela chave do item. O bloco é substituído inteiro a cada
 * gravação (o web manda o bloco completo, já mesclado).
 */
export const OnboardingPreferencesSchema = z.object({
  welcomeSeenAt: isoDateTimeSchema.optional(),
  tourDoneAt: isoDateTimeSchema.optional(),
  tourSkippedAt: isoDateTimeSchema.optional(),
  checklistDismissedAt: isoDateTimeSchema.optional(),
  /** Versão do onboarding em que o bloco foi gravado (ausente vale 1). */
  version: z.number().int().min(1).optional(),
  steps: z
    .record(z.string().max(64).regex(ONBOARDING_STEP_KEY_PATTERN), isoDateTimeSchema)
    .refine((steps) => Object.keys(steps).length <= ONBOARDING_MAX_STEPS, {
      error: 'Itens demais na lista.',
    })
    .optional(),
})
export type OnboardingPreferences = z.infer<typeof OnboardingPreferencesSchema>

/**
 * Preferências do financeiro. `firstStepsDismissedAt`: quando a pessoa dispensou o cartão
 * "Primeiros passos do financeiro" da visão geral (ausente = o cartão aparece enquanto faltar
 * algum passo).
 */
export const FinancePreferencesSchema = z.object({
  firstStepsDismissedAt: isoDateTimeSchema.optional(),
})
export type FinancePreferences = z.infer<typeof FinancePreferencesSchema>

export const PORTFOLIO_KPIS_PREFERENCES = ['shown', 'hidden'] as const
export const PortfolioKpisPreferenceSchema = z.enum(PORTFOLIO_KPIS_PREFERENCES, {
  error: 'Use mostrar ou ocultar.',
})
export type PortfolioKpisPreference = z.infer<typeof PortfolioKpisPreferenceSchema>

export const UserPreferencesSchema = z.object({
  theme: z.enum(THEME_PREFERENCES, { error: 'Escolha um tema válido.' }),
  density: z.enum(DENSITY_PREFERENCES, { error: 'Escolha uma densidade válida.' }),
  /** Onde o botão do WhatsApp abre a conversa (Sprint 3). Ausente = `web`. */
  whatsappTarget: WhatsappTargetSchema.optional(),
  /**
   * Modo lado a lado (Sprint 14): layout compacto, fluxo rápido e barra de retorno. Ausente =
   * `false`; a API sempre devolve o valor (`preferencesOf`).
   */
  whatsappSideBySide: z.boolean({ error: 'Use verdadeiro ou falso.' }).optional(),
  /**
   * Notificações (Sprint 7). Ausente = padrão; a API sempre devolve o bloco completo
   * (`notificationPreferencesOf`).
   */
  notifications: NotificationPreferencesSchema.optional(),
  /**
   * Onboarding guiado. Ausente = nada visto ainda. Na leitura é tolerante: um bloco inválido vira
   * ausente sem derrubar as outras preferências (`catch`); na gravação é validado (ver
   * `UpdatePreferencesRequestSchema`).
   */
  onboarding: OnboardingPreferencesSchema.optional().catch(undefined),
  /** Financeiro (primeiros passos dispensados). Tolerante na leitura, como o onboarding. */
  finance: FinancePreferencesSchema.optional().catch(undefined),
  /**
   * Indicadores da carteira mostrados ou escondidos (U07). Ausente: escondidos abaixo de 640 px e
   * para quem tem cobradora vinculada, mostrados para os demais. Tolerante na leitura.
   */
  portfolioKpis: PortfolioKpisPreferenceSchema.optional().catch(undefined),
})
export type UserPreferences = z.infer<typeof UserPreferencesSchema>

export const DEFAULT_USER_PREFERENCES: Readonly<UserPreferences> = Object.freeze({
  theme: 'system',
  density: 'comfortable',
  whatsappTarget: 'wame',
  whatsappSideBySide: false,
  notifications: DEFAULT_NOTIFICATION_PREFERENCES,
})

/** Sprint 13: `key` aceita perfis personalizados além dos 5 do sistema. */
export const RoleSummarySchema = z.object({
  key: RoleKeyStringSchema,
  name: z.string(),
})
export type RoleSummary = z.infer<typeof RoleSummarySchema>

/** Usuário autenticado como a API devolve (sem hash, permissões já resolvidas pelo perfil). */
export const UserDtoSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  email: z.email(),
  role: RoleSummarySchema,
  permissions: z.array(PermissionSchema),
  collectorId: z.uuid().optional(),
  unitId: z.uuid().optional(),
  preferences: UserPreferencesSchema,
  mustChangePassword: z.boolean(),
  lastLoginAt: isoDateTimeSchema.optional(),
})
export type UserDto = z.infer<typeof UserDtoSchema>

/** Linha da administração de usuários (`GET /users`). */
export const AdminUserDtoSchema = UserDtoSchema.extend({
  isActive: z.boolean(),
  createdAt: isoDateTimeSchema,
})
export type AdminUserDto = z.infer<typeof AdminUserDtoSchema>

const userNameSchema = z
  .string({ error: 'Informe o nome.' })
  .trim()
  .min(2, { error: 'Informe o nome completo.' })
  .max(120, { error: 'O nome pode ter no máximo 120 caracteres.' })

export const CreateUserRequestSchema = z.object({
  name: userNameSchema,
  email: emailSchema,
  roleKey: RoleKeyStringSchema,
  collectorId: z.uuid({ error: 'Cobradora inválida.' }).optional(),
  unitId: z.uuid({ error: 'Unidade inválida.' }).optional(),
})
export type CreateUserRequest = z.infer<typeof CreateUserRequestSchema>

/** A senha temporária aparece só nesta resposta; o usuário troca no primeiro acesso. */
export const CreateUserResponseSchema = z.object({
  user: AdminUserDtoSchema,
  temporaryPassword: z.string(),
})
export type CreateUserResponse = z.infer<typeof CreateUserResponseSchema>

const NOTHING_TO_UPDATE = 'Informe ao menos um campo para atualizar.'

const hasAnyValue = (value: Record<string, unknown>) =>
  Object.values(value).some((field) => field !== undefined)

/** `null` em `collectorId`/`unitId` remove o vínculo. */
export const UpdateUserRequestSchema = z
  .object({
    name: userNameSchema.optional(),
    roleKey: RoleKeyStringSchema.optional(),
    collectorId: z.uuid({ error: 'Cobradora inválida.' }).nullable().optional(),
    unitId: z.uuid({ error: 'Unidade inválida.' }).nullable().optional(),
    isActive: z.boolean().optional(),
  })
  .refine(hasAnyValue, { error: NOTHING_TO_UPDATE })
export type UpdateUserRequest = z.infer<typeof UpdateUserRequestSchema>

/**
 * `notifications` vai como bloco completo (o web manda o bloco atual com a mudança); a API ainda
 * mescla campo a campo com o salvo (`mergeNotificationPreferences`).
 */
export const UpdatePreferencesRequestSchema = UserPreferencesSchema.partial()
  .extend({
    onboarding: OnboardingPreferencesSchema.optional(),
    finance: FinancePreferencesSchema.optional(),
    portfolioKpis: PortfolioKpisPreferenceSchema.optional(),
  })
  .refine(hasAnyValue, { error: NOTHING_TO_UPDATE })
export type UpdatePreferencesRequest = z.infer<typeof UpdatePreferencesRequestSchema>
